/**
 * HistorySubTab.tsx
 *
 * History tab matching legacy quest-lab-list.php HISTORY tab.
 * Chart.js line chart + table matrix. View toggle: Chart (default) vs Table.
 *
 * Test selection uses a dedicated picker panel: search + source filter + a
 * scrollable list where each biomarker shows its latest value and how many
 * data points it has, plus a removable "selected" chip row (color-matched to
 * the chart lines). This replaces the old flat wrap of hundreds of checkboxes.
 */
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Check } from 'lucide-react';
import { getPatientBiomarkers } from '@/services/labs.service';
import { Button, Spinner } from '@/components/ui';
import { DatePicker } from '@/components/ui/DatePicker';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value + 'T00:00:00');
  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const CHART_COLORS = [
  '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
  '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
];

interface TestMeta {
  count: number;
  latestValue: string;
  latestUnit: string | null;
  latestDate: string;
}

interface HistorySubTabProps {
  patientId: number;
  preSelectedTest?: string | null;
}

export function HistorySubTab({ patientId, preSelectedTest }: HistorySubTabProps) {
  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<any>(null);

  const { data: allResults, isLoading } = useQuery({
    queryKey: ['patient-biomarkers', patientId],
    queryFn: () => getPatientBiomarkers(patientId),
    staleTime: 5 * 60 * 1000,
  });

  const results = useMemo(() => {
    let rows = allResults ?? [];
    if (dateFrom) rows = rows.filter((r) => r.date >= dateFrom);
    if (dateTo) rows = rows.filter((r) => r.date <= dateTo);
    return rows;
  }, [allResults, dateFrom, dateTo]);

  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const r of results) if (r.source) set.add(r.source);
    return Array.from(set).sort();
  }, [results]);

  // Per-test rollup: how many points it has and its most-recent value/unit.
  const testMeta = useMemo(() => {
    const map = new Map<string, TestMeta>();
    for (const r of results) {
      if (sourceFilter && r.source !== sourceFilter) continue;
      const existing = map.get(r.testName);
      if (!existing) {
        map.set(r.testName, { count: 1, latestValue: r.value, latestUnit: r.unit, latestDate: r.date });
      } else {
        existing.count += 1;
        if (r.date > existing.latestDate) {
          existing.latestDate = r.date;
          existing.latestValue = r.value;
          existing.latestUnit = r.unit;
        }
      }
    }
    return map;
  }, [results, sourceFilter]);

  const testNames = useMemo(() => Array.from(testMeta.keys()).sort(), [testMeta]);

  const filteredTestNames = useMemo(() => {
    if (!searchFilter.trim()) return testNames;
    const q = searchFilter.toLowerCase();
    return testNames.filter((t) => t.toLowerCase().includes(q));
  }, [testNames, searchFilter]);

  const allDates = useMemo(() => {
    const dates = new Set<string>();
    for (const p of results) if (p.date) dates.add(p.date);
    return Array.from(dates).sort();
  }, [results]);

  const selectedData = useMemo(() => {
    if (selectedTests.size === 0) return [];
    return results.filter((p) => selectedTests.has(p.testName));
  }, [results, selectedTests]);

  // Selection order drives chart line colors; reuse it in the picker so the
  // color dots match the chart/legend.
  const selectedOrder = useMemo(() => Array.from(selectedTests), [selectedTests]);
  const colorForTest = useCallback((name: string): string | null => {
    const idx = selectedOrder.indexOf(name);
    return idx >= 0 ? CHART_COLORS[idx % CHART_COLORS.length] : null;
  }, [selectedOrder]);

  useEffect(() => {
    if (preSelectedTest && testNames.includes(preSelectedTest)) {
      setSelectedTests(new Set([preSelectedTest]));
    }
  }, [preSelectedTest, testNames]);

  function toggleTest(name: string) {
    setSelectedTests((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  function selectAllFiltered() {
    setSelectedTests((prev) => {
      const next = new Set(prev);
      for (const name of filteredTestNames) next.add(name);
      return next;
    });
  }
  function deselectAll() { setSelectedTests(new Set()); }

  const loadChartJs = useCallback((): Promise<any> => {
    return new Promise((resolve, reject) => {
      if ((window as any).Chart) { resolve((window as any).Chart); return; }
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4/dist/chart.umd.min.js';
      script.onload = () => resolve((window as any).Chart);
      script.onerror = () => reject(new Error('Failed to load Chart.js'));
      document.head.appendChild(script);
    });
  }, []);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current || selectedTests.size === 0 || viewMode !== 'chart') return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    try {
      const ChartClass = await loadChartJs();

      const datasets = Array.from(selectedTests).map((testName, idx) => {
        const testData = results.filter((r) => r.testName === testName);
        const dataPoints = allDates.map((date) => {
          const entry = testData.find((r) => r.date === date);
          if (!entry) return null;
          const numStr = entry.value.replace(/[^0-9.\-]/g, '');
          const num = parseFloat(numStr);
          return Number.isFinite(num) ? num : null;
        });

        return {
          label: testName,
          data: dataPoints,
          borderColor: CHART_COLORS[idx % CHART_COLORS.length],
          backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] + '33',
          tension: 0.2,
          spanGaps: true,
          pointRadius: 4,
          pointHoverRadius: 6,
        };
      });

      chartRef.current = new ChartClass(canvasRef.current, {
        type: 'line',
        data: {
          labels: allDates.map((d) => formatDate(d)),
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: selectedTests.size <= 15,
              position: 'right',
            },
            tooltip: { mode: 'index', intersect: false },
          },
          scales: {
            x: { display: true },
            y: { display: true, beginAtZero: false },
          },
        },
      });
    } catch {
      // Chart.js CDN load failed
    }
  }, [selectedTests, results, allDates, viewMode, loadChartJs]);

  useEffect(() => {
    if (viewMode === 'chart') {
      void buildChart();
    }
    return () => {
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    };
  }, [buildChart, viewMode]);

  function clearFilters() {
    setSearchFilter('');
    setSourceFilter('');
    setDateFrom('');
    setDateTo('');
  }

  return (
    <div className="space-y-4">
      {/* Visualization toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border border-slate-300 p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('chart')}
            className={`rounded px-3 py-1 text-sm font-medium transition-colors ${viewMode === 'chart' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            Chart
          </button>
          <button
            type="button"
            onClick={() => setViewMode('table')}
            className={`rounded px-3 py-1 text-sm font-medium transition-colors ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            Table
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          <DatePicker size="sm" ariaLabel="From date" value={dateFrom} onChange={setDateFrom} wrapperClassName="w-36" />
          <span className="text-xs text-slate-400">to</span>
          <DatePicker size="sm" ariaLabel="To date" value={dateTo} onChange={setDateTo} wrapperClassName="w-36" />
        </div>
        {(searchFilter || sourceFilter || dateFrom || dateTo) && (
          <Button size="sm" variant="ghost" onClick={clearFilters}>Clear</Button>
        )}
        <span className="ml-auto text-xs text-slate-500">
          <span className="font-semibold text-slate-700">{selectedTests.size}</span> of {testNames.length} tests selected
        </span>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : (allResults ?? []).length === 0 ? (
        <p className="py-4 text-sm text-slate-400 italic">No historical test data available.</p>
      ) : (
        <>
          {/* Selected chips */}
          {selectedTests.size > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {selectedOrder.map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white py-1 pl-2 pr-1 text-xs text-slate-700 shadow-sm"
                >
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: colorForTest(name) ?? '#cbd5e1' }} />
                  <span className="max-w-[220px] truncate" title={name}>{name}</span>
                  <button
                    type="button"
                    onClick={() => toggleTest(name)}
                    className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label={`Remove ${name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <button type="button" onClick={deselectAll} className="text-xs font-medium text-slate-500 hover:text-slate-700">
                Clear all
              </button>
            </div>
          )}

          <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
            {/* Test picker */}
            <div className="flex w-full shrink-0 flex-col rounded-xl border border-slate-200 bg-white shadow-sm lg:w-80">
              <div className="space-y-2 border-b border-slate-100 p-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    placeholder="Search tests..."
                    className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                {sources.length > 1 && (
                  <select
                    value={sourceFilter}
                    onChange={(e) => setSourceFilter(e.target.value)}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">All sources</option>
                    {sources.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">{filteredTestNames.length} tests</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={selectAllFiltered}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:text-slate-300"
                      disabled={filteredTestNames.length === 0}
                    >
                      Select all
                    </button>
                    <span className="text-slate-200">|</span>
                    <button
                      type="button"
                      onClick={deselectAll}
                      className="text-xs font-medium text-slate-500 hover:text-slate-700 disabled:text-slate-300"
                      disabled={selectedTests.size === 0}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>

              <div className="max-h-[420px] min-h-[220px] flex-1 overflow-y-auto p-1">
                {filteredTestNames.length === 0 ? (
                  <p className="px-3 py-6 text-center text-sm text-slate-400 italic">No tests match your search.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {filteredTestNames.map((name) => {
                      const meta = testMeta.get(name);
                      const selected = selectedTests.has(name);
                      const color = colorForTest(name);
                      return (
                        <li key={name}>
                          <button
                            type="button"
                            onClick={() => toggleTest(name)}
                            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors ${selected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                          >
                            <span
                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${selected ? 'border-transparent' : 'border-slate-300 bg-white'}`}
                              style={selected ? { backgroundColor: color ?? '#2563eb' } : undefined}
                            >
                              {selected && <Check className="h-3 w-3 text-white" />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm text-slate-800" title={name}>{name}</span>
                              {meta && (
                                <span className="block truncate text-xs text-slate-400">
                                  {meta.latestValue}{meta.latestUnit ? ` ${meta.latestUnit}` : ''} · {meta.count} {meta.count === 1 ? 'result' : 'results'}
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* Visualization */}
            <div className="min-w-0 flex-1">
              {selectedTests.size === 0 ? (
                <div className="flex h-full min-h-[300px] items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50">
                  <p className="text-sm text-slate-400 italic">Select tests from the list to view history.</p>
                </div>
              ) : viewMode === 'chart' ? (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4" style={{ height: 460 }}>
                  <canvas ref={canvasRef} />
                </div>
              ) : (
                <div className="max-h-[460px] overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-3 sticky left-0 bg-slate-50 z-10">Date</th>
                        {Array.from(selectedTests).map((t) => (
                          <th key={t} className="px-4 py-3 min-w-[100px]">{t}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {allDates.slice().reverse().map((date) => {
                        const dayPoints = selectedData.filter((p) => p.date === date);
                        if (dayPoints.length === 0) return null;
                        const byTest = new Map<string, string>();
                        for (const p of dayPoints) byTest.set(p.testName, p.value);
                        return (
                          <tr key={date} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-slate-700 sticky left-0 bg-white font-medium">{formatDate(date)}</td>
                            {Array.from(selectedTests).map((t) => (
                              <td key={t} className={`px-4 py-3 ${byTest.has(t) ? 'text-slate-900 bg-green-50' : 'text-slate-300'}`}>
                                {byTest.get(t) ?? '—'}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
